import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Select, MenuItem, Tooltip, TextField } from '@mui/material';
import type { SelectProps } from '@mui/material/Select';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useForm } from 'react-hook-form';
import notify from 'lib/notify';
import { FormTextField } from './form';

export type EmailItem = {
  email: string;
  type: string;
};

type FormData = {
  emails: EmailItem[];
  primaryEmail: string | null;
};

type InlineConfigBase<V> = {
  value: V;
  onChange: (v: V) => void;
  onCommit: (v: V) => Promise<boolean> | void;
  onCancel: () => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
};

type EmailsInlineConfig = InlineConfigBase<EmailItem[]> & {
  selectZIndex?: number;
  onSaveField?: (fieldName: string, value: unknown) => Promise<void>;
  initialPrimaryEmail?: string;
};

const primaryIconStyle = { height: 20, width: 20, cursor: 'pointer' };

const emptyEmailRow = (): EmailItem => ({ email: '', type: 'personal' });

const ensureEmptyRow = (rows: EmailItem[]): EmailItem[] =>
  rows.some(r => r.email === '') ? rows : [...rows, emptyEmailRow()];

const nonEmpty = (rows: EmailItem[]): EmailItem[] => rows.filter(r => r.email !== '');

const EMPTY_EMAIL_ARRAY: EmailItem[] = [];

const EmailsManager = ({
  initialEmails = EMPTY_EMAIL_ARRAY,
  initialPrimaryEmail,
  onChangeEmails,
  onChangePrimaryEmail,
  triggerSave,
  inlineConfig,
  menuProps: menuPropsOverride,
}: {
  initialEmails?: EmailItem[];
  initialPrimaryEmail?: string;
  onChangeEmails?: (emails: EmailItem[]) => void;
  onChangePrimaryEmail?: (primaryEmail?: string) => void;
  triggerSave?: () => void;
  inlineConfig?: EmailsInlineConfig;
  menuProps?: SelectProps['MenuProps'];
}) => {
  const isInline = !!inlineConfig;
  const inputRef = useRef<HTMLInputElement | null>(null);

  // ---------- Non-inline state (react-hook-form) ----------
  const { control, setValue, getValues, watch, trigger } = useForm<FormData>({
    defaultValues: {
      emails: initialEmails,
      primaryEmail: initialPrimaryEmail || null,
    },
  });

  const formEmails = watch('emails');
  const formPrimaryEmail = watch('primaryEmail');

  // ---------- Inline state ----------
  const [inlineEmails, setInlineEmails] = useState<EmailItem[]>(() =>
    ensureEmptyRow(inlineConfig?.value?.length ? [...inlineConfig.value] : []),
  );
  const [inlinePrimaryEmail, setInlinePrimaryEmail] = useState<string | null | undefined>(
    inlineConfig?.initialPrimaryEmail ?? null,
  );
  const [inlinePrimaryIndex, setInlinePrimaryIndex] = useState<number | null>(() => {
    if (!inlineConfig?.initialPrimaryEmail) return null;
    return (inlineConfig?.value || []).findIndex(e => e.email === inlineConfig.initialPrimaryEmail);
  });

  useEffect(() => {
    if (isInline) return; // inline state initialized via useState, managed internally
    // Non-inline: sync from props when they change
  }, [isInline]);

  useEffect(() => {
    if (isInline && inlineConfig?.autoFocus !== false) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isInline, inlineConfig?.autoFocus]);

  const menuProps =
    menuPropsOverride ?? (isInline ? { sx: { zIndex: inlineConfig?.selectZIndex } } : undefined);

  const handleSelectMouseDown = (e: React.MouseEvent) => {
    // Prevent mousedown from stealing focus from the text input.
    // If the input blurs, addEmptyInput() fires → state update → re-render,
    // which can race with ClickAwayListener and close the inline editor.
    e.preventDefault();
    e.stopPropagation();
  };

  const emails = isInline ? inlineEmails : formEmails;
  const primaryEmail = isInline ? inlinePrimaryEmail : formPrimaryEmail;

  const getEmails = (newEmails: EmailItem[]) => newEmails.filter(email => email.email !== '');

  const addEmptyInput = useCallback(() => {
    if (isInline) {
      setInlineEmails(prev => ensureEmptyRow([...prev]));
    } else {
      const currentEmails = getValues('emails');
      if (!currentEmails.some(e => e.email === '')) {
        setValue('emails', [...currentEmails, emptyEmailRow()]);
      }
    }
  }, [isInline, getValues, setValue]);

  useEffect(() => {
    if (!isInline) {
      addEmptyInput();
    }
  }, [initialEmails, addEmptyInput, isInline]);

  const removeEmail = (index: number) => {
    if (isInline) {
      const newEmails = inlineEmails.filter((_, i) => i !== index);
      if (inlinePrimaryIndex != null) {
        if (index === inlinePrimaryIndex) {
          setInlinePrimaryIndex(null);
        } else if (index < inlinePrimaryIndex) {
          setInlinePrimaryIndex(inlinePrimaryIndex - 1);
        }
      }
      const withEmpty = ensureEmptyRow(newEmails);
      setInlineEmails(withEmpty);
      inlineConfig?.onChange(nonEmpty(withEmpty));
    } else {
      const currentEmails = getValues('emails');
      const newEmails = currentEmails.filter((_, i) => i !== index);
      if (!newEmails.some(e => e.email === getValues('primaryEmail'))) {
        setValue('primaryEmail', null);
        onChangePrimaryEmail?.(undefined);
      }
      setValue('emails', newEmails);
      onChangeEmails?.(getEmails(newEmails));
    }
  };

  const onInputChange = async (index: number, value: string) => {
    if (value === '') {
      removeEmail(index);
      return;
    }

    if (isInline) {
      const newEmails = [...inlineEmails];
      const newEmail = { email: value, type: newEmails[index]?.type ?? 'personal' };
      newEmails[index] = newEmail;
      const withEmpty = ensureEmptyRow(newEmails);
      setInlineEmails(withEmpty);
      inlineConfig?.onChange(nonEmpty(withEmpty));

      // Sync primary when editing the current primary email's text
      if (inlinePrimaryIndex === index && newEmail.email !== '') {
        setInlinePrimaryEmail(newEmail.email);
      }

      // Auto-set primary for single non-empty email
      if (nonEmpty(withEmpty).length === 1 && newEmail.email !== '') {
        setInlinePrimaryIndex(0);
        setInlinePrimaryEmail(newEmail.email);
        try {
          await inlineConfig?.onSaveField?.('emails', nonEmpty(withEmpty));
          await inlineConfig?.onSaveField?.('primaryEmail', newEmail.email);
        } catch (error) {
          notify(error);
          setInlinePrimaryIndex(null);
          setInlinePrimaryEmail(inlineConfig?.initialPrimaryEmail ?? null);
        }
      }
    } else {
      const isValid = await trigger(`emails.${index}.email`);
      const currentEmails = getValues('emails');
      const newEmails = [...currentEmails];
      const newEmail = { email: value, type: newEmails[index].type };
      newEmails[index] = newEmail;

      if (newEmails.filter(e => e.email !== '').length === 1 && newEmail.email !== '') {
        if (isValid) {
          setValue('primaryEmail', newEmail.email);
          onChangePrimaryEmail?.(newEmail.email);
        }
      }

      setValue('emails', newEmails);
      if (isValid) {
        onChangeEmails?.(getEmails(newEmails));
      }
    }
  };

  const onTypeChange = (index: number, typeValue: string) => {
    if (isInline) {
      const newEmails = [...inlineEmails];
      newEmails[index] = { ...newEmails[index], type: typeValue };
      const withEmpty = ensureEmptyRow(newEmails);
      setInlineEmails(withEmpty);
      inlineConfig?.onChange(nonEmpty(withEmpty));
    } else {
      const currentEmails = getValues('emails');
      const newEmails = [...currentEmails];
      newEmails[index] = { ...newEmails[index], type: typeValue };
      setValue('emails', newEmails);
      onChangeEmails?.(getEmails(newEmails));
    }
  };

  const handleSetPrimary = async (index: number) => {
    const email = emails[index]?.email;
    if (!email) return;
    if (isInline) {
      const prevIndex = inlinePrimaryIndex;
      const prevEmail = inlinePrimaryEmail;
      setInlinePrimaryIndex(index);
      setInlinePrimaryEmail(email);
      try {
        await inlineConfig?.onSaveField?.('emails', nonEmpty(ensureEmptyRow(inlineEmails)));
        await inlineConfig?.onSaveField?.('primaryEmail', email);
      } catch (error) {
        notify(error);
        setInlinePrimaryIndex(prevIndex);
        setInlinePrimaryEmail(prevEmail);
      }
    } else {
      setValue('primaryEmail', email);
      onChangePrimaryEmail?.(email);
    }
  };

  const handleClearPrimary = async () => {
    if (isInline) {
      const prevIndex = inlinePrimaryIndex;
      const prevEmail = inlinePrimaryEmail;
      setInlinePrimaryIndex(null);
      setInlinePrimaryEmail(null);
      try {
        await inlineConfig?.onSaveField?.('emails', nonEmpty(ensureEmptyRow(inlineEmails)));
        await inlineConfig?.onSaveField?.('primaryEmail', null);
      } catch (error) {
        notify(error);
        setInlinePrimaryIndex(prevIndex);
        setInlinePrimaryEmail(prevEmail);
      }
    } else {
      setValue('primaryEmail', null);
      onChangePrimaryEmail?.(undefined);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (isInline) {
        await inlineConfig?.onCommit?.(nonEmpty(ensureEmptyRow(inlineEmails)));
      } else if (triggerSave) {
        triggerSave();
      }
    } else if (e.key === 'Escape') {
      if (isInline) {
        e.stopPropagation();
        e.preventDefault();
        inlineConfig?.onCancel();
      }
    }
  };

  return (
    <>
      {emails.map((email, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Select
            value={email.type}
            onChange={e => onTypeChange(index, e.target.value)}
            sx={{ width: '110px' }}
            size="small"
            disabled={isInline ? inlineConfig?.disabled : false}
            MenuProps={menuProps}
            onMouseDown={handleSelectMouseDown}
          >
            <MenuItem value="personal">Personal</MenuItem>
            <MenuItem value="work">Work</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>

          {isInline ? (
            <TextField
              inputRef={index === 0 ? inputRef : undefined}
              size="small"
              value={email.email}
              onChange={e => onInputChange(index, e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={inlineConfig?.disabled}
              error={inlineConfig?.hasError}
              sx={{ flex: 1 }}
            />
          ) : (
            <FormTextField
              name={`emails.${index}.email`}
              control={control}
              sx={{ flex: '1' }}
              onChange={e => onInputChange(index, e.target.value)}
              onFocus={addEmptyInput}
              onBlur={() => addEmptyInput()}
              onKeyDown={handleKeyDown}
            />
          )}

          {isInline
            ? inlinePrimaryIndex === index
              ? (<CheckCircleIcon style={primaryIconStyle} onClick={handleClearPrimary} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }} />)
              : (
                <Tooltip title="Set as Primary">
                  <RadioButtonUncheckedIcon
                    style={primaryIconStyle}
                    onClick={() => handleSetPrimary(index)}
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                  />
                </Tooltip>
              )
            : primaryEmail === email.email
              ? (<CheckCircleIcon style={primaryIconStyle} onClick={handleClearPrimary} />)
              : (
                <Tooltip title="Set as Primary">
                  <RadioButtonUncheckedIcon
                    style={primaryIconStyle}
                    onClick={() => handleSetPrimary(index)}
                  />
                </Tooltip>
              )}
        </div>
      ))}
    </>
  );
};

export default EmailsManager;
